// MARQUEE selection — drag a 2D rectangle on a frozen view-aligned
// plane in front of the user. On release, every voxel that appears
// inside the rect from the user's viewpoint is selected: corners are
// PERSPECTIVE-projected from the press-time eye onto the plane, so the
// selection is the frustum the user actually sees through the rectangle
// (depth-agnostic — a far voxel behind a near one is just as selectable).
// The plane + eye are frozen at press so head motion during the drag
// doesn't swim the gesture.
//
// The plane is captured at press time and stays fixed in world space
// for the duration of the drag, so head motion during the gesture
// doesn't swim the rect around.

import * as THREE from "three";
import {
  controllerStates,
  controllers,
  laserLocalDir,
} from "../../input/controllers";
import { CELL_SIZE, type VoxelGrid } from "../../model/voxel-grid";
import type { Selection } from "../../model/selection";
import { interaction, setInteraction } from "../state";
import type { Interaction } from "../types";

const _scratchOrigin = new THREE.Vector3();
const _scratchDir = new THREE.Vector3();
const _scratchCamPos = new THREE.Vector3();
const _scratchCamFwd = new THREE.Vector3();
const _scratchCamRight = new THREE.Vector3();
const _scratchCamUp = new THREE.Vector3();
const _worldUp = new THREE.Vector3(0, 1, 0);

// Start a marquee. Captures a camera-aligned plane anchored at the END
// OF THE LASER (its current draw distance) and the press point in
// plane-local (r, u) coords. No-op if the controller's laser doesn't
// intersect the plane (extremely glancing angle).
//
// The plane stays camera-facing so the rect reads as a screen-space
// selection window; its depth along the view axis is cosmetic — the
// selection is an orthographic projection onto the plane, invariant to
// where the plane sits along the normal — so anchoring it at the laser
// tip just draws the window where the user is pointing.
export function startMarquee(
  controllerIndex: number,
  cam: THREE.Camera,
  laserEndDist: number,
): void {
  // Camera basis at press time.
  cam.getWorldPosition(_scratchCamPos);
  _scratchCamFwd.set(0, 0, -1).transformDirection(cam.matrixWorld).normalize();
  _scratchCamRight
    .copy(_scratchCamFwd)
    .cross(_worldUp)
    .normalize();
  // If the camera is looking straight up/down, cross with world-up
  // degenerates. Fall back to camera's local +X.
  if (_scratchCamRight.lengthSq() < 1e-4) {
    _scratchCamRight.set(1, 0, 0).transformDirection(cam.matrixWorld).normalize();
  }
  _scratchCamUp.copy(_scratchCamRight).cross(_scratchCamFwd).normalize();

  // Anchor the plane at the laser's endpoint: controller origin + laser
  // direction × the current draw distance.
  const c = controllers[controllerIndex];
  c.getWorldPosition(_scratchOrigin);
  laserLocalDir(controllerIndex, _scratchDir);
  _scratchDir.transformDirection(c.matrixWorld).normalize();
  const planeOrigin = _scratchOrigin
    .clone()
    .addScaledVector(_scratchDir, laserEndDist);
  // planeNormal points toward the camera (so the plane "front" faces
  // the user). Projection uses dot with right/up so the sign doesn't
  // matter for that — but storing the convention makes the gesture's
  // intent explicit.
  const planeNormal = _scratchCamFwd.clone().negate();
  const planeRight = _scratchCamRight.clone();
  const planeUp = _scratchCamUp.clone();

  // Project the controller's current ray onto the plane to get the
  // initial corner.
  const initial = pickRayOnPlane(controllerIndex, planeOrigin, planeNormal);
  if (!initial) return;
  const dx = initial.x - planeOrigin.x;
  const dy = initial.y - planeOrigin.y;
  const dz = initial.z - planeOrigin.z;
  const startR = dx * planeRight.x + dy * planeRight.y + dz * planeRight.z;
  const startU = dx * planeUp.x + dy * planeUp.y + dz * planeUp.z;

  setInteraction({
    kind: "marquee_select",
    controllerIndex,
    planeOrigin,
    planeRight,
    planeUp,
    planeNormal,
    // Eye position at press — apex of the perspective selection frustum.
    eyeWorld: _scratchCamPos.clone(),
    startR,
    startU,
    currentR: startR,
    currentU: startU,
  });
}

// Per-frame: update the current corner from the controller's current
// laser-on-plane intersection. We deliberately do NOT refresh the selection
// here — voxels highlight only once the marquee is released (commitMarquee),
// so the in-progress box stays visually quiet. Caller still invokes the rect
// (violet box) redraw separately.
export function handleMarqueeDrag(): void {
  if (interaction.kind !== "marquee_select") return;
  const state = interaction;
  if (!controllerStates[state.controllerIndex].selecting) return;

  const hit = pickRayOnPlane(
    state.controllerIndex,
    state.planeOrigin,
    state.planeNormal,
  );
  if (!hit) return;
  const dx = hit.x - state.planeOrigin.x;
  const dy = hit.y - state.planeOrigin.y;
  const dz = hit.z - state.planeOrigin.z;
  const newR =
    dx * state.planeRight.x + dy * state.planeRight.y + dz * state.planeRight.z;
  const newU =
    dx * state.planeUp.x + dy * state.planeUp.y + dz * state.planeUp.z;
  if (newR === state.currentR && newU === state.currentU) return;
  state.currentR = newR;
  state.currentU = newU;
}

// Commit the current marquee on selectend — this is the ONLY place the
// selection updates for a marquee gesture (there is no live preview during
// the drag), so the voxels highlight exactly when the box is released.
// Replaces the previous selection.
export function commitMarquee(
  grid: VoxelGrid,
  gridGroup: THREE.Object3D,
  selection: Selection,
): void {
  if (interaction.kind !== "marquee_select") return;
  selection.replace(computeMarqueePicked(interaction, grid, gridGroup));
}

// Cells covered by the current marquee rect. A voxel is included if ANY
// part of it appears inside the rect from the user's viewpoint: we
// PERSPECTIVE-project all 8 corners of the cell's cube from the press-
// time eye onto the captured plane (the ray eye→corner crossed with the
// plane), take the (r, u) bounding box of that footprint, and test it
// for overlap with the rect. This makes the selection a frustum from the
// eye — "what you see in the box" — rather than a parallel/orthographic
// prism, which mis-selects voxels at depths away from the plane.
const _voxelWorld = new THREE.Vector3();
function computeMarqueePicked(
  state: Extract<Interaction, { kind: "marquee_select" }>,
  grid: VoxelGrid,
  gridGroup: THREE.Object3D,
): { ix: number; iy: number; iz: number }[] {
  const rMin = Math.min(state.startR, state.currentR);
  const rMax = Math.max(state.startR, state.currentR);
  const uMin = Math.min(state.startU, state.currentU);
  const uMax = Math.max(state.startU, state.currentU);

  const eye = state.eyeWorld;
  const n = state.planeNormal;
  const o = state.planeOrigin;
  const right = state.planeRight;
  const up = state.planeUp;
  // Plane: X·n = o·n. Ray eye→corner hits it at s = (o·n − eye·n)/(dir·n).
  const oN = o.x * n.x + o.y * n.y + o.z * n.z;
  const eyeN = eye.x * n.x + eye.y * n.y + eye.z * n.z;

  gridGroup.updateMatrixWorld(true);
  const picked: { ix: number; iy: number; iz: number }[] = [];
  for (const [ix, iy, iz] of grid.entries()) {
    // Perspective-project the 8 corners; track the (r, u) footprint.
    let vrMin = Infinity;
    let vrMax = -Infinity;
    let vuMin = Infinity;
    let vuMax = -Infinity;
    let anyInFront = false;
    for (let cx = ix; cx <= ix + 1; cx++) {
      for (let cy = iy; cy <= iy + 1; cy++) {
        for (let cz = iz; cz <= iz + 1; cz++) {
          _voxelWorld.set(cx * CELL_SIZE, cy * CELL_SIZE, cz * CELL_SIZE);
          _voxelWorld.applyMatrix4(gridGroup.matrixWorld);
          const dirx = _voxelWorld.x - eye.x;
          const diry = _voxelWorld.y - eye.y;
          const dirz = _voxelWorld.z - eye.z;
          // n points back toward the eye, so a corner in front of the eye
          // has dir·n < 0. Skip corners behind the eye (can't be seen).
          const denom = dirx * n.x + diry * n.y + dirz * n.z;
          if (denom >= -1e-6) continue;
          const s = (oN - eyeN) / denom;
          // Hit point of ray eye→corner on the plane, in (r, u) coords.
          const hx = eye.x + s * dirx - o.x;
          const hy = eye.y + s * diry - o.y;
          const hz = eye.z + s * dirz - o.z;
          const r = hx * right.x + hy * right.y + hz * right.z;
          const u = hx * up.x + hy * up.y + hz * up.z;
          if (r < vrMin) vrMin = r;
          if (r > vrMax) vrMax = r;
          if (u < vuMin) vuMin = u;
          if (u > vuMax) vuMax = u;
          anyInFront = true;
        }
      }
    }
    // AABB overlap between the voxel's projected footprint and the rect.
    if (
      anyInFront &&
      vrMax >= rMin &&
      vrMin <= rMax &&
      vuMax >= uMin &&
      vuMin <= uMax
    ) {
      picked.push({ ix, iy, iz });
    }
  }
  return picked;
}

// Intersect controller i's laser with the given plane (origin + normal).
// Returns the world-space hit point, or null if the ray is parallel or
// hits behind the controller.
function pickRayOnPlane(
  controllerIndex: number,
  planeOrigin: THREE.Vector3,
  planeNormal: THREE.Vector3,
): THREE.Vector3 | null {
  const c = controllers[controllerIndex];
  c.getWorldPosition(_scratchOrigin);
  laserLocalDir(controllerIndex, _scratchDir);
  _scratchDir.transformDirection(c.matrixWorld).normalize();
  const denom =
    _scratchDir.x * planeNormal.x +
    _scratchDir.y * planeNormal.y +
    _scratchDir.z * planeNormal.z;
  if (Math.abs(denom) < 1e-6) return null;
  const dx = planeOrigin.x - _scratchOrigin.x;
  const dy = planeOrigin.y - _scratchOrigin.y;
  const dz = planeOrigin.z - _scratchOrigin.z;
  const t =
    (dx * planeNormal.x + dy * planeNormal.y + dz * planeNormal.z) / denom;
  if (t < 0) return null;
  return _scratchOrigin.addScaledVector(_scratchDir, t);
}
